"use client";

import React from "react";
import { useTranslate } from "../../lib/translation";
import styles from "./Label.module.css";

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** id of the form control this label describes. */
  htmlFor: string;
  /** Native tooltip text; used as a fallback when `title` is not set. */
  tooltipText?: string;
  /** Appends a "*" marker plus an sr-only hint, `requiredText` or the translated "(required)". It only changes the label text; the control still needs its own required. @default false */
  required?: boolean;
  /** Screen-reader hint read after a required label. Defaults to the translated "(required)" (`label.required`). */
  requiredText?: string;
  /** Dims the label and shows a not-allowed cursor. @default false */
  disabled?: boolean;
  /**
   * `field` names a control above or beside it (medium weight); `option` is
   * the text of a checkbox, radio or switch, which reads as plain text.
   * @default "field"
   */
  variant?: "field" | "option";
}

/** Form field label with required, disabled, and tooltip affordances. */
const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  (
    {
      htmlFor,
      children,
      className = "",
      tooltipText,
      required,
      requiredText,
      disabled = false,
      variant = "field",
      title,
      ...rest
    },
    ref,
  ) => {
    const t = useTranslate();
    return (
      <label
        ref={ref}
        htmlFor={htmlFor}
        className={[styles.label, variant === "option" ? styles.option : "", disabled ? styles.disabled : "", className]
          .filter(Boolean)
          .join(" ")}
        title={title || tooltipText}
        {...rest}
      >
        {children}
        {required && (
          <>
            <span aria-hidden="true" className={styles.required}>
              *
            </span>
            <span className={styles.srOnly}>
              {requiredText ?? t("label.required", "(required)")}
            </span>
          </>
        )}
      </label>
    );
  },
);

Label.displayName = "Label";

export default Label;
